import React, { useEffect, useState, Suspense } from "react";
import { Route, Routes, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import "./scss/style.scss";
import { loginUser } from "./store/services/operations/loginAPi";
import CommonLoader from "./components/commonLoader";

// Lazy-loaded components
const DefaultLayout = React.lazy(() => import("./layout/DefaultLayout"));
const Page404 = React.lazy(() => import("./views/pages/page404/Page404"));
const OnboardingWizard = React.lazy(() =>
  import("./views/pages/onboarding/OnboardingWizard")
);

const App = () => {
  const dispatch = useDispatch();
  const { isAuthenticated } = useSelector((state) => state.auth);
  const userInfo = useSelector((state) => state.auth.user);
  const navigate = useNavigate();
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);

  // Check user authentication on app load
  useEffect(() => {
    const checkAuth = async () => {
      if (!isAuthenticated) {
        try {
          await dispatch(loginUser()).unwrap();
          // Only redirect to audience-insights if onboarding is complete OR if accountType is AGENCY
          if (userInfo && (userInfo.onboarding_complete === true || userInfo.accountType === "AGENCY")) {
            navigate("/audience-insights");
          } else if (userInfo && userInfo.onboarding_complete === false && userInfo.accountType !== "AGENCY") {
            setShowOnboarding(true);
          }
        } catch (error) {
          let redirectUrl;
          if (process.env.REACT_APP_ENV === "development") {
            redirectUrl = "http://dev.katalisia.com/";
          } else if (process.env.NODE_ENV === "development") {
            redirectUrl = "http://localhost:3000/";
          } else {
            redirectUrl = "http://www.katalisia.com/";
          }
          window.location.href = redirectUrl;
        } finally {
          setIsCheckingAuth(false);
        }
      } else {
        // For already authenticated users, check onboarding status
        // Skip onboarding for AGENCY account types
        if (userInfo && userInfo.onboarding_complete === false && userInfo.accountType !== "AGENCY") {
          setShowOnboarding(true);
        } else if (userInfo && (userInfo.onboarding_complete === true || userInfo.accountType === "AGENCY")) {
          if (window.location.pathname !== "/audience-insights") {
            navigate("/audience-insights");
          }
        }
        setIsCheckingAuth(false);
      }
    };

    checkAuth();
  }, [dispatch, isAuthenticated, userInfo]);

  const handleOnboardingComplete = (userData) => {
    setShowOnboarding(false);
    navigate("/audience-insights");
  };

  if (isCheckingAuth) {
    return <CommonLoader />;
  }

  const loading = (
    <div className="pt-3 text-center">
      <div className="sk-spinner sk-spinner-pulse"></div>
    </div>
  );

  return (
    <Suspense fallback={loading}>
      {/* Onboarding Wizard Modal - Only render here, not in OnboardingController */}
      {isAuthenticated && (
        <OnboardingWizard
          visible={showOnboarding}
          onComplete={handleOnboardingComplete}
        />
      )}

      <Routes>
        {!isAuthenticated ? (
          <Route path="/404" element={<Page404 />} />
        ) : (
          <Route path="*" element={<DefaultLayout />} />
        )}
      </Routes>
    </Suspense>
  );
};

export default App;
